import { useState, useMemo, useRef } from 'react'
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { api } from '@/api/client'
import { useCreateCampaign, useScheduleLimit } from '@/api/hooks/usePoster'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { cn } from '@/lib/utils'
import {
  Megaphone, XIcon, Upload, Trash2, Plus, ChevronRight, ChevronLeft,
  Instagram, Facebook, CheckCheck, Search, Clock, Layers, Rocket,
  Video, Image as ImageIcon, FolderUp, RefreshCw, CalendarRange, FolderOpen,
  Scissors, Pencil, Tag, Youtube, Eye, Sparkles, Loader2,
} from 'lucide-react'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import type { Account } from '@/types'
import { useYouTubeChannels, useYouTubeDefaultDescription } from '@/api/hooks/useYouTube'
import { useOpenAIStatus, useGenerateCaption } from '@/api/hooks/useOpenAI'
import { captionPresets } from './captionPresets'
import { CaptionPresetEditor } from './CaptionPresetEditor'
import { useCaptionPresets } from '@/api/hooks/useCaptionPresets'
import { FolderTreePicker } from '@/components/FolderTreePicker'

interface ContentItem {
  id: string
  message: string
  title: string
  description: string
  imageUrl: string
  mediaType: 'image' | 'video'
  uploading: boolean
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  accounts: Account[]
}

function getShuffledCaptions(presets: Record<string, string[]>, tone: string, count: number): string[] {
  const pool = [...(presets[tone] || presets['casual'] || [])]
  if (pool.length === 0) return Array(count).fill('')
  // Full-cycle: use all captions before repeating
  const result: string[] = []
  let remaining: string[] = []
  for (let i = 0; i < count; i++) {
    if (remaining.length === 0) {
      remaining = [...pool]
      // Fisher-Yates shuffle
      for (let j = remaining.length - 1; j > 0; j--) {
        const k = Math.floor(Math.random() * (j + 1));
        [remaining[j], remaining[k]] = [remaining[k], remaining[j]]
      }
    }
    result.push(remaining.shift()!)
  }
  return result
}

export function CampaignDialog({ open, onOpenChange, accounts }: Props) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)

  // Step 1: Content
  const [contentItems, setContentItems] = useState<ContentItem[]>([])
  const [selectedTone, setSelectedTone] = useState('casual')
  const multiUploadRef = useRef<HTMLInputElement>(null)
  const [contentSource, setContentSource] = useState<'manual' | 'library'>('manual')
  const [selectedFolderId, setSelectedFolderId] = useState('')
  const [imageCount, setImageCount] = useState(5)
  const [recyclable, setRecyclable] = useState(false)
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set())
  const { data: mediaFolders } = useMediaFolders()

  // Folder picker state
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  // Custom captions per library image (keyed by image id)
  const [libraryCaptions, setLibraryCaptions] = useState<Record<string, string>>({})

  // Step 2: Accounts
  const [selectedAccounts, setSelectedAccounts] = useState<Record<string, { ig: boolean; fb: boolean }>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [tagFilter, setTagFilter] = useState('all')

  // YouTube
  const [selectedYtChannels, setSelectedYtChannels] = useState<Record<string, boolean>>({})
  const [ytTitle, setYtTitle] = useState('')
  const [ytDescription, setYtDescription] = useState('')
  const [ytTags, setYtTags] = useState('')
  const [ytPrivacy, setYtPrivacy] = useState<'public' | 'unlisted' | 'private'>('public')
  const { data: ytChannels } = useYouTubeChannels()
  const firstSelectedYtChannelId = useMemo(() => {
    const entry = Object.entries(selectedYtChannels).find(([, v]) => v)
    return entry ? entry[0] : null
  }, [selectedYtChannels])
  const firstSelectedYtChannel = ytChannels?.find(ch => ch.id === firstSelectedYtChannelId)
  const { data: ytDefaultDesc } = useYouTubeDefaultDescription(firstSelectedYtChannelId)

  // Step 3: Schedule
  const [scheduleDate, setScheduleDate] = useState('')
  const [startTime, setStartTime] = useState('06:00')
  const [endTime, setEndTime] = useState('18:00')
  const [intervalMinutes, setIntervalMinutes] = useState(30)
  const [includeStories, setIncludeStories] = useState(false)
  const [storyContentIndex, setStoryContentIndex] = useState(0)
  const [category, setCategory] = useState('Campanha')
  const [multiDay, setMultiDay] = useState(false)
  const [multiDayStart, setMultiDayStart] = useState('')
  const [totalDays, setTotalDays] = useState(30)
  const [postTime, setPostTime] = useState('10:00')
  const [imagesPerDay, setImagesPerDay] = useState(1)
  const [intervalMin, setIntervalMin] = useState(5)
  const [intervalMax, setIntervalMax] = useState(30)

  // Clip sequence mode
  const [clipMode, setClipMode] = useState(false)
  const [clipMorningTime, setClipMorningTime] = useState('10:00')
  const [clipAfternoonTime, setClipAfternoonTime] = useState('14:00')
  const [clipDistribution, setClipDistribution] = useState<'sequential' | 'shuffled'>('sequential')

  const [antiFingerprint, setAntiFingerprint] = useState(false)
  const [ctaSendMessage, setCtaSendMessage] = useState(false)

  // Title review modal
  const [titleReviewOpen, setTitleReviewOpen] = useState(false)
  const [bulkDescription, setBulkDescription] = useState('')

  // Preferred profile
  const [preferredProfile, setPreferredProfile] = useState('')

  // Custom caption
  const [customCaption, setCustomCaption] = useState('')
  const [presetEditorOpen, setPresetEditorOpen] = useState(false)
  const { data: serverPresets } = useCaptionPresets()
  const activePresets = serverPresets || captionPresets

  const DEFAULT_TONE_LABELS: Record<string, string> = {
    seducao: 'Seducao',
    casual: 'Casual',
    religioso: 'Religioso',
    motivacional: 'Motivacional',
  }
  const dynamicTones = useMemo(() => {
    const tones: Array<{ value: string; label: string }> = []
    // Defaults first
    for (const key of ['seducao', 'casual', 'religioso', 'motivacional']) {
      tones.push({ value: key, label: DEFAULT_TONE_LABELS[key] })
    }
    // Custom tones from presets
    for (const key of Object.keys(activePresets)) {
      if (!DEFAULT_TONE_LABELS[key]) {
        tones.push({ value: key, label: key.charAt(0).toUpperCase() + key.slice(1) })
      }
    }
    // Sem legenda + Personalizado always last
    tones.push({ value: 'none', label: 'Sem legenda' })
    tones.push({ value: 'personalizado', label: 'Personalizado' })
    return tones
  }, [activePresets])

  const createCampaign = useCreateCampaign()
  const { data: scheduleLimit } = useScheduleLimit()
  const { data: aiStatus } = useOpenAIStatus()
  const generateCaptionMut = useGenerateCaption()

  const firstSelectedIgUserId = useMemo(() => {
    for (const [igUserId, p] of Object.entries(selectedAccounts)) {
      if (p.ig || p.fb) return igUserId
    }
    return null
  }, [selectedAccounts])

  const handleGenerateCaption = async (itemId: string) => {
    if (!firstSelectedIgUserId) { toast.error('Selecione ao menos uma conta primeiro'); return }
    try {
      const { caption } = await generateCaptionMut.mutateAsync({ igUserId: firstSelectedIgUserId })
      updateContent(itemId, 'message', caption)
      toast.success('Legenda gerada!')
    } catch (err) {
      toast.error((err as Error).message || 'Erro ao gerar legenda')
    }
  }

  // --- Step 1 helpers ---
  const addContentItem = () => {
    setContentItems(prev => [...prev, {
      id: crypto.randomUUID(),
      message: '',
      title: '',
      description: '',
      imageUrl: '',
      mediaType: 'image',
      uploading: false,
    }])
  }

  const updateContent = (id: string, field: keyof ContentItem, value: string | boolean) => {
    setContentItems(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c))
  }

  const removeContent = (id: string) => {
    setContentItems(prev => prev.filter(c => c.id !== id))
  }

  const handleContentUpload = async (id: string, file: File) => {
    updateContent(id, 'uploading', true)
    try {
      const result = await api.upload(file, { filter: false })
      updateContent(id, 'imageUrl', result.url)
      if (result.type) updateContent(id, 'mediaType', result.type)
      toast.success(result.type === 'video' ? 'Video enviado!' : 'Imagem enviada!')
    } catch {
      toast.error('Erro ao enviar arquivo')
    } finally {
      updateContent(id, 'uploading', false)
    }
  }

  const handleMultiUpload = async (files: FileList) => {
    const captions = selectedTone === 'none'
      ? Array(files.length).fill('')
      : selectedTone === 'personalizado'
        ? Array(files.length).fill(customCaption)
        : getShuffledCaptions(activePresets, selectedTone, files.length)
    const newItems: ContentItem[] = Array.from(files).map((_, i) => ({
      id: crypto.randomUUID(),
      message: captions[i] || '',
      title: '',
      description: '',
      imageUrl: '',
      mediaType: 'image' as const,
      uploading: true,
    }))
    setContentItems(prev => [...prev, ...newItems])

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const itemId = newItems[i].id
      try {
        const result = await api.upload(file, { filter: false })
        setContentItems(prev => prev.map(c => c.id === itemId ? {
          ...c,
          imageUrl: result.url,
          mediaType: result.type || 'image',
          uploading: false,
        } : c))
      } catch {
        setContentItems(prev => prev.map(c => c.id === itemId ? { ...c, uploading: false } : c))
        toast.error(`Erro ao enviar ${file.name}`)
      }
    }
    toast.success(`${files.length} arquivo(s) enviado(s)!`)
  }

  const redistributeCaptions = () => {
    if (selectedTone === 'none') {
      setContentItems(prev => prev.map(item => ({ ...item, message: '' })))
    } else if (selectedTone === 'personalizado') {
      setContentItems(prev => prev.map(item => ({ ...item, message: customCaption })))
    } else {
      const captions = getShuffledCaptions(activePresets, selectedTone, contentItems.length)
      setContentItems(prev => prev.map((item, i) => ({ ...item, message: captions[i] || '' })))
    }
    toast.success('Legendas redistribuidas!')
  }

  // --- Step 2 helpers ---
  const allTags = useMemo(() => {
    const tags = new Set<string>()
    accounts.forEach(a => a.tags?.forEach(t => tags.add(t)))
    return Array.from(tags).sort()
  }, [accounts])

  const filteredAccounts = useMemo(() => {
    return accounts.filter(a => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        if (!a.name?.toLowerCase().includes(q) && !a.username?.toLowerCase().includes(q)) return false
      }
      if (tagFilter !== 'all' && !a.tags?.includes(tagFilter)) return false
      return true
    })
  }, [accounts, searchQuery, tagFilter])

  const toggleAccount = (igUserId: string, platform: 'ig' | 'fb') => {
    setSelectedAccounts(prev => {
      const current = prev[igUserId] || { ig: false, fb: false }
      return { ...prev, [igUserId]: { ...current, [platform]: !current[platform] } }
    })
  }

  const selectAllFiltered = () => {
    const updated = { ...selectedAccounts }
    filteredAccounts.forEach(a => {
      updated[a.igUserId] = { ig: !a.fbOnly, fb: true }
    })
    setSelectedAccounts(updated)
  }

  const toggleYtChannel = (channelId: string) => {
    setSelectedYtChannels(prev => ({ ...prev, [channelId]: !prev[channelId] }))
  }

  const clearAll = () => {
    setSelectedAccounts({})
    setSelectedYtChannels({})
  }

  const selectedCount = useMemo(() => {
    let count = 0
    for (const v of Object.values(selectedAccounts)) {
      if (v.ig) count++
      if (v.fb) count++
    }
    for (const v of Object.values(selectedYtChannels)) {
      if (v) count++
    }
    return count
  }, [selectedAccounts, selectedYtChannels])

  const hasYTSelected = useMemo(() => {
    return Object.values(selectedYtChannels).some(v => v)
  }, [selectedYtChannels])

  const selectedPageCount = useMemo(() => {
    return Object.values(selectedAccounts).filter(v => v.ig || v.fb).length
  }, [selectedAccounts])

  // Unique profiles across selected accounts
  const availableProfiles = useMemo(() => {
    const names = new Set<string>()
    for (const igUserId of Object.keys(selectedAccounts)) {
      const sel = selectedAccounts[igUserId]
      if (!sel.ig && !sel.fb) continue
      const acc = accounts.find(a => a.igUserId === igUserId)
      acc?.tokens?.forEach(t => { if (t.valid) names.add(t.profileName) })
    }
    return Array.from(names).sort()
  }, [selectedAccounts, accounts])

  // --- Step 4: Preview ---
  const preview = useMemo(() => {
    if (multiDay) {
      const feedPerDay = selectedCount * imagesPerDay
      const storyPerDay = includeStories ? selectedCount : 0
      const totalPerDay = feedPerDay + storyPerDay
      const totalPosts = totalPerDay * totalDays
      const endDate = multiDayStart ? (() => {
        const d = new Date(multiDayStart + 'T00:00:00')
        d.setDate(d.getDate() + totalDays - 1)
        return d.toISOString().split('T')[0]
      })() : ''
      return {
        totalSlots: totalDays,
        accountsPerSlot: selectedCount,
        storyPosts: storyPerDay * totalDays,
        feedPosts: feedPerDay * totalDays,
        totalPosts,
        endDate,
        multiDay: true as const,
        imagesPerDay,
      }
    }

    const totalSlots = intervalMinutes > 0
      ? Math.max(1, Math.floor(((new Date(`2026-01-01T${endTime}:00`).getTime()) - (new Date(`2026-01-01T${startTime}:00`).getTime())) / (intervalMinutes * 60000)) + 1)
      : 1
    const feedAccounts = selectedCount
    const accountsPerSlot = Math.ceil(feedAccounts / totalSlots)
    const storyPosts = includeStories ? selectedCount : 0
    const feedPosts = feedAccounts

    return { totalSlots, accountsPerSlot, storyPosts, feedPosts, totalPosts: storyPosts + feedPosts, endDate: '', multiDay: false as const, imagesPerDay: 1 }
  }, [selectedCount, startTime, endTime, intervalMinutes, includeStories, multiDay, totalDays, multiDayStart, imagesPerDay])

  // --- Build payload ---
  const buildAccountsPayload = () => {
    const result: Array<{ igUserId: string; platforms: string[] }> = []
    for (const [igUserId, platforms] of Object.entries(selectedAccounts)) {
      const plats: string[] = []
      if (platforms.ig) plats.push('ig')
      if (platforms.fb) plats.push('fb')
      if (plats.length > 0) result.push({ igUserId, platforms: plats })
    }
    return result
  }

  const buildYtPayload = () => {
    return Object.entries(selectedYtChannels).filter(([, v]) => v).map(([id]) => id)
  }

  const submitCampaign = (forceRecycle = false) => {
    const accountsPayload = buildAccountsPayload()
    const ytPayload = buildYtPayload()
    if (accountsPayload.length === 0 && ytPayload.length === 0) { toast.error('Selecione ao menos uma conta'); return }

    if (scheduleLimit && !scheduleLimit.unlimited && scheduleLimit.remaining !== null && scheduleLimit.remaining <= 0) {
      toast.error(`Limite de agendamentos atingido (${scheduleLimit.current}/${scheduleLimit.limit}). Exclua agendamentos antigos ou aguarde a publicacao.`)
      return
    }

    if (contentSource === 'library') {
      if (!selectedFolderId) { toast.error('Selecione uma pasta'); return }
      if (selectedImageIds.size === 0) { toast.error('Selecione ao menos uma imagem'); return }
    } else {
      const validContent = contentItems.filter(c => c.imageUrl)
      if (validContent.length === 0) { toast.error('Adicione ao menos um conteudo com midia'); return }
    }

    if (!forceRecycle) {
      if (clipMode) {
        if (!scheduleDate) { toast.error('Selecione a data'); return }
        const totalClips = folderClips ? Object.values(folderClips).reduce((sum, c) => sum + c.length, 0) : 0
        if (!confirm(`Criar campanha de cortes com ${totalClips} clips em ${selectedPageCount} paginas?`)) return
      } else if (multiDay) {
        if (!multiDayStart) { toast.error('Selecione a data de inicio'); return }
        if (totalDays < 1) { toast.error('Minimo 1 dia'); return }
        if (!confirm(`Criar campanha multi-dia com ${preview.totalPosts} posts em ${totalDays} dias?`)) return
      } else {
        if (!scheduleDate) { toast.error('Selecione a data'); return }
        if (!confirm(`Criar campanha com ${preview.totalPosts} posts em ${selectedPageCount} paginas?`)) return
      }
    }

    const validContent = contentItems.filter(c => c.imageUrl)
    const imageIdsArray = [...selectedImageIds]

    createCampaign.mutate({
      contentItems: contentSource === 'library'
        ? []
        : validContent.map(c => ({
          id: c.id,
          message: c.message,
          imageUrl: c.imageUrl,
          mediaType: c.mediaType,
          title: c.title || undefined,
          description: c.description || undefined,
        })),
      accounts: accountsPayload,
      scheduleDate: clipMode ? scheduleDate : (multiDay ? multiDayStart : scheduleDate),
      startTime: clipMode ? clipMorningTime : (multiDay ? postTime : startTime),
      endTime: clipMode ? clipAfternoonTime : (multiDay ? postTime : endTime),
      intervalMinutes: clipMode ? 1 : (multiDay ? 0 : intervalMinutes),
      includeStories,
      storyContentIndex,
      category,
      ...(selectedTone === 'personalizado' && customCaption ? { customCaption } : {}),
      ...(selectedTone === 'none' ? { captionTone: 'none' } : {}),
      ...(clipMode ? {
        clipMode: true,
        fromLibrary: true,
        folderId: selectedFolderId,
        captionTone: selectedTone === 'personalizado' ? 'casual' : selectedTone,
        clipMorningTime,
        clipAfternoonTime,
        includeStories: false,
        clipDistribution,
        ...(forceRecycle ? { forceRecycle: true } : {}),
      } : {}),
      ...(multiDay && !clipMode ? { multiDay: true, startDate: multiDayStart, totalDays, postTime, imagesPerDay, intervalMin, intervalMax } : {}),
      ...(!clipMode && contentSource === 'library' ? {
        fromLibrary: true,
        folderId: selectedFolderId,
        imageCount: imageIdsArray.length,
        selectedImageIds: imageIdsArray,
        captionTone: selectedTone === 'personalizado' ? 'casual' : selectedTone,
        libraryCaptions: Object.keys(libraryCaptions).length > 0 ? libraryCaptions : undefined,
        ...(forceRecycle ? { forceRecycle: true } : {}),
      } : {}),
      ...(ytPayload.length > 0 ? {
        youtubeChannels: ytPayload,
        youtubeTitle: undefined,
        youtubeDescription: ytDefaultDesc?.defaultDescription || undefined,
        youtubeTags: ytTags.trim()
          ? ytTags.split(',').map(t => t.trim()).filter(Boolean)
          : ytDefaultDesc?.defaultTags
            ? ytDefaultDesc.defaultTags.split(',').map(t => t.trim()).filter(Boolean)
            : undefined,
        youtubePrivacy: ytPrivacy,
      } : {}),
      ...(preferredProfile ? { preferredProfile } : {}),
      ...(antiFingerprint ? { antiFingerprint: true } : {}),
      ...(ctaSendMessage ? { ctaSendMessage: true } : {}),
    }, {
      onSuccess: (data: any) => {
        if (data.recycleNeeded) {
          if (confirm('Todas as imagens selecionadas ja foram usadas. Deseja reciclar o historico e continuar?')) {
            submitCampaign(true)
          }
          return
        }
        const ytInfo = data.ytPosts ? ` (${data.ytPosts} YT)` : ''
        toast.success(`Campanha criada! ${data.totalPosts} posts em ${data.pagesCount} paginas${ytInfo}`)
        if (data.recycled) {
          toast.info('Historico de imagens reciclado para esta campanha.', { duration: 4000 })
        }
        resetForm()
        onOpenChange(false)
      },
      onError: (err) => toast.error(err.message || 'Erro ao criar campanha'),
    })
  }

  const handleSubmit = () => submitCampaign(false)

  const resetForm = () => {
    setStep(1)
    setContentItems([])
    setSelectedTone('casual')
    setContentSource('manual')
    setSelectedFolderId('')
    setFolderPickerOpen(false)
    setLibraryCaptions({})
    setImageCount(5)
    setRecyclable(false)
    setSelectedImageIds(new Set())
    setSelectedAccounts({})
    setSearchQuery('')
    setTagFilter('all')
    setScheduleDate('')
    setStartTime('06:00')
    setEndTime('18:00')
    setIntervalMinutes(30)
    setIncludeStories(true)
    setStoryContentIndex(0)
    setCategory('Campanha')
    setMultiDay(false)
    setMultiDayStart('')
    setTotalDays(30)
    setPostTime('10:00')
    setImagesPerDay(1)
    setIntervalMin(5)
    setIntervalMax(30)
    setClipMode(false)
    setClipMorningTime('10:00')
    setClipAfternoonTime('14:00')
    setClipDistribution('sequential')
    setCustomCaption('')
    setTitleReviewOpen(false)
    setBulkDescription('')
    setSelectedYtChannels({})
    setYtTitle('')
    setYtDescription('')
    setYtTags('')
    setYtPrivacy('public')
  }

  const canNext = () => {
    if (step === 1) {
      if (contentSource === 'library') return !!selectedFolderId
      return contentItems.some(c => c.imageUrl)
    }
    if (step === 2) return selectedCount > 0
    if (step === 3) {
      if (clipMode) return !!scheduleDate
      return multiDay ? !!multiDayStart && totalDays > 0 : !!scheduleDate
    }
    return true
  }

  const selectedFolderData = mediaFolders?.find(f => f.id === selectedFolderId)

  // Auto-select all images when folder changes
  const prevFolderRef = useRef(selectedFolderId)
  if (selectedFolderId !== prevFolderRef.current) {
    prevFolderRef.current = selectedFolderId
    const folder = mediaFolders?.find(f => f.id === selectedFolderId)
    if (folder) {
      setSelectedImageIds(new Set(folder.images.map(img => img.id)))
    } else {
      setSelectedImageIds(new Set())
    }
  }

  const toggleImageSelection = (imgId: string) => {
    setSelectedImageIds(prev => {
      const next = new Set(prev)
      if (next.has(imgId)) next.delete(imgId)
      else next.add(imgId)
      return next
    })
  }

  const selectAllImages = () => {
    if (!selectedFolderData) return
    setSelectedImageIds(new Set(selectedFolderData.images.map(img => img.id)))
  }

  const deselectAllImages = () => {
    setSelectedImageIds(new Set())
  }

  // Detect clip sequences in selected folder
  const folderClips = useMemo(() => {
    if (!selectedFolderData) return null
    const clips = selectedFolderData.images.filter(img => img.clip)
    if (clips.length === 0) return null
    // Group by source, sort by part
    const grouped: Record<string, typeof clips> = {}
    for (const clip of clips) {
      const src = clip.clip!.source
      if (!grouped[src]) grouped[src] = []
      grouped[src].push(clip)
    }
    for (const src of Object.keys(grouped)) {
      grouped[src].sort((a, b) => a.clip!.part - b.clip!.part)
    }
    return grouped
  }, [selectedFolderData])

  const hasClips = !!folderClips && Object.keys(folderClips).length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto p-0"
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <VisuallyHidden><DialogTitle>Campanha</DialogTitle></VisuallyHidden>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            <span className="text-base font-semibold">Campanha</span>
            <Badge variant="outline" className="text-[11px]">Etapa {step}/4</Badge>
          </div>
          <DialogClose className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <XIcon className="h-4 w-4" />
          </DialogClose>
        </div>

        {/* Step indicators */}
        <div className="flex gap-1 px-5 pt-3">
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors',
                s <= step ? 'bg-primary' : 'bg-muted',
              )}
            />
          ))}
        </div>

        <div className="px-5 pb-5">
          {/* ========== STEP 1: Content ========== */}
          {step === 1 && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">Conteudos</h3>
                  <p className="text-[11px] text-muted-foreground">Selecione a fonte das imagens</p>
                </div>
              </div>

              {/* Source toggle */}
              <div className="flex gap-1 rounded-lg border border-border p-1">
                <button
                  type="button"
                  onClick={() => setContentSource('manual')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    contentSource === 'manual' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Upload className="h-3 w-3" /> Upload Manual
                </button>
                <button
                  type="button"
                  onClick={() => setContentSource('library')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    contentSource === 'library' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <FolderOpen className="h-3 w-3" /> Da Biblioteca
                </button>
              </div>

              {/* Tone selector (shared) */}
              <div className="flex flex-wrap items-center gap-2">
                <Select value={selectedTone} onValueChange={setSelectedTone}>
                  <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Teor" /></SelectTrigger>
                  <SelectContent>
                    {dynamicTones.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  onClick={() => setPresetEditorOpen(true)}
                >
                  <Pencil className="h-3 w-3" /> Gerenciar Frases
                </Button>
              </div>

              {selectedTone === 'personalizado' && (
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">Legenda personalizada</Label>
                  <Textarea
                    value={customCaption}
                    onChange={(e) => setCustomCaption(e.target.value)}
                    placeholder="Digite a legenda que sera usada em todos os posts..."
                    className="min-h-[60px] text-xs resize-none"
                  />
                </div>
              )}

              {contentSource === 'library' ? (
                /* ===== Library mode ===== */
                <div className="space-y-3">
                  <div>
                    <Label className="mb-1.5 text-xs text-muted-foreground">Pasta</Label>
                    {/* Selected folder display / toggle picker */}
                    <button
                      type="button"
                      onClick={() => setFolderPickerOpen(!folderPickerOpen)}
                      className={cn(
                        'flex h-9 w-full items-center justify-between rounded-md border px-3 text-xs transition-colors',
                        folderPickerOpen ? 'border-primary' : 'border-input hover:border-primary/50',
                      )}
                    >
                      {selectedFolderData ? (
                        <span className="flex items-center gap-1.5 min-w-0">
                          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate">{selectedFolderData.name}</span>
                          <span className="text-muted-foreground shrink-0">({selectedFolderData.images.length})</span>
                          {selectedFolderData.tags?.slice(0, 1).map(tag => (
                            <span key={tag} className="text-[10px] bg-muted px-1 rounded shrink-0">{tag}</span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Selecione uma pasta...</span>
                      )}
                      <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform', folderPickerOpen && 'rotate-90')} />
                    </button>

                    {/* Inline folder picker */}
                    {folderPickerOpen && mediaFolders && (
                      <FolderTreePicker
                        folders={mediaFolders}
                        selectedId={selectedFolderId}
                        onSelect={(id) => { setSelectedFolderId(id); setFolderPickerOpen(false) }}
                        className="mt-1.5"
                      />
                    )}
                  </div>

                  {/* Clip sequence mode toggle */}
                  {hasClips && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 p-3">
                        <div className="flex items-center gap-2">
                          <Scissors className="h-4 w-4 text-primary" />
                          <div>
                            <p className="text-xs font-medium">Campanha de Cortes</p>
                            <p className="text-[10px] text-muted-foreground">1 video por dia, cortes manha + tarde, em sequencia</p>
                          </div>
                        </div>
                        <Switch checked={clipMode} onCheckedChange={setClipMode} />
                      </div>
                      {clipMode && selectedPageCount > 1 && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setClipDistribution('sequential')}
                            className={cn(
                              'rounded-lg border p-2.5 text-left transition-all',
                              clipDistribution === 'sequential'
                                ? 'border-cyan-500/40 bg-cyan-500/10 ring-1 ring-cyan-500/20'
                                : 'border-border hover:border-muted-foreground/30'
                            )}
                          >
                            <p className="text-[11px] font-medium">Sequencial</p>
                            <p className="text-[10px] text-muted-foreground">Todas paginas recebem os mesmos clips em ordem (parte 1, 2, 3...)</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setClipDistribution('shuffled')}
                            className={cn(
                              'rounded-lg border p-2.5 text-left transition-all',
                              clipDistribution === 'shuffled'
                                ? 'border-violet-500/40 bg-violet-500/10 ring-1 ring-violet-500/20'
                                : 'border-border hover:border-muted-foreground/30'
                            )}
                          >
                            <p className="text-[11px] font-medium">Embaralhado</p>
                            <p className="text-[10px] text-muted-foreground">Cada pagina recebe clips diferentes, sem repetir no mesmo dia</p>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Clip sequence preview */}
                  {clipMode && folderClips ? (
                    <div className="space-y-2">
                      {clipDistribution === 'shuffled' && selectedPageCount > 1 ? (
                        <p className="text-[11px] text-muted-foreground">
                          {Object.values(folderClips).reduce((sum, c) => sum + c.length, 0)} clips distribuidos em {selectedPageCount} paginas
                          {' '}({Math.ceil(Object.values(folderClips).reduce((sum, c) => sum + c.length, 0) / selectedPageCount)} por pagina,
                          {' '}{Math.ceil(Math.ceil(Object.values(folderClips).reduce((sum, c) => sum + c.length, 0) / selectedPageCount) / 10)} dia{Math.ceil(Math.ceil(Object.values(folderClips).reduce((sum, c) => sum + c.length, 0) / selectedPageCount) / 10) > 1 ? 's' : ''})
                        </p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">
                          {Object.keys(folderClips).length} video{Object.keys(folderClips).length > 1 ? 's' : ''} = {Object.keys(folderClips).length} dia{Object.keys(folderClips).length > 1 ? 's' : ''} de publicacao
                        </p>
                      )}
                      {Object.entries(folderClips).map(([source, clips], dayIdx) => {
                        const half = Math.ceil(clips.length / 2)
                        const morningCount = Math.min(half, 5)
                        const afternoonCount = Math.min(clips.length - morningCount, 5)
                        return (
                          <div key={source} className="rounded-lg border border-border p-2.5">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">Dia {dayIdx + 1}</Badge>
                              <span className="text-[11px] font-medium">{source}</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                              Manha: {morningCount} corte{morningCount > 1 ? 's' : ''} (1-{morningCount})
                              {afternoonCount > 0 && <> | Tarde: {afternoonCount} corte{afternoonCount > 1 ? 's' : ''} ({morningCount + 1}-{morningCount + afternoonCount})</>}
                              {' '}| 1min entre cada
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  ) : selectedFolderData && selectedFolderData.images.length > 0 ? (
                    /* Folder preview thumbnails with selection + optional captions */
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <p className="text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">{selectedImageIds.size}</span> de {selectedFolderData.images.length} selecionadas
                          {Object.keys(libraryCaptions).filter(k => libraryCaptions[k]?.trim() && selectedImageIds.has(k)).length > 0 && (
                            <span className="ml-1 text-teal-500">
                              ({Object.keys(libraryCaptions).filter(k => libraryCaptions[k]?.trim() && selectedImageIds.has(k)).length} com legenda)
                            </span>
                          )}
                        </p>
                        <div className="flex gap-1">
                          <button type="button" onClick={selectAllImages} className="text-[10px] text-primary hover:underline">Todas</button>
                          <span className="text-[10px] text-muted-foreground">|</span>
                          <button type="button" onClick={deselectAllImages} className="text-[10px] text-muted-foreground hover:underline">Nenhuma</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 max-h-[280px] overflow-y-auto">
                        {selectedFolderData.images.map(img => {
                          const isSelected = selectedImageIds.has(img.id)
                          return (
                            <div key={img.id} className={cn(
                              'overflow-hidden rounded-lg border transition-all cursor-pointer',
                              isSelected
                                ? libraryCaptions[img.id]?.trim() ? 'border-teal-400/50 bg-teal-500/5 ring-1 ring-teal-400/30' : 'border-primary/40 ring-1 ring-primary/20'
                                : 'border-border opacity-40 grayscale',
                            )}>
                              <div className="aspect-square overflow-hidden relative" onClick={() => toggleImageSelection(img.id)}>
                                {img.mediaType === 'video' ? (
                                  <video src={img.url} className="h-full w-full object-cover" muted />
                                ) : (
                                  <img src={img.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                )}
                                <div className={cn(
                                  'absolute top-1 right-1 h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors',
                                  isSelected ? 'bg-primary border-primary' : 'bg-black/30 border-white/50',
                                )}>
                                  {isSelected && <CheckCheck className="h-2.5 w-2.5 text-primary-foreground" />}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="p-1">
                                  <input
                                    type="text"
                                    value={libraryCaptions[img.id] || ''}
                                    onChange={e => setLibraryCaptions(prev => ({ ...prev, [img.id]: e.target.value }))}
                                    placeholder="Legenda..."
                                    className="w-full bg-transparent text-[10px] outline-none placeholder:text-muted-foreground/40 border-none p-0.5"
                                  />
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      <p className="mt-1 text-[10px] text-muted-foreground/60">
                        Clique para selecionar/deselecionar. Legenda vazia usa o teor selecionado.
                      </p>
                    </div>
                  ) : null}

                  {(!mediaFolders || mediaFolders.length === 0) && (
                    <div className="rounded-lg border-2 border-dashed border-border/50 p-6 text-center">
                      <FolderOpen className="mx-auto h-8 w-8 text-muted-foreground/20" />
                      <p className="mt-2 text-xs text-muted-foreground">
                        Crie pastas na aba "Biblioteca" do Poster primeiro
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* ===== Manual mode ===== */
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={addContentItem}>
                      <Plus className="h-3 w-3" /> Adicionar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1 text-xs"
                      onClick={() => multiUploadRef.current?.click()}
                    >
                      <FolderUp className="h-3.5 w-3.5" /> Upload Multiplo
                    </Button>
                    <input
                      ref={multiUploadRef}
                      type="file"
                      multiple
                      accept="image/*,video/mp4,video/quicktime,video/webm"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          handleMultiUpload(e.target.files)
                          e.target.value = ''
                        }
                      }}
                    />
                    {contentItems.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 gap-1 text-xs text-muted-foreground"
                        onClick={redistributeCaptions}
                      >
                        <RefreshCw className="h-3 w-3" /> Redistribuir legendas
                      </Button>
                    )}
                  </div>

                  {contentItems.length === 0 && (
                    <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border/50 py-10">
                      <Layers className="h-8 w-8 text-muted-foreground/30" />
                      <p className="text-xs text-muted-foreground">Nenhum conteudo adicionado</p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={addContentItem}>
                          <Plus className="h-3 w-3" /> Adicionar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-xs"
                          onClick={() => multiUploadRef.current?.click()}
                        >
                          <FolderUp className="h-3.5 w-3.5" /> Upload Multiplo
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="max-h-[400px] space-y-3 overflow-y-auto">
                {contentItems.map((item, idx) => (
                  <div key={item.id} className="rounded-lg border border-border p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Conteudo {idx + 1}</span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => removeContent(item.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>

                    <div className="flex gap-2">
                      <label className={cn(
                        'flex w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed p-2 transition-colors hover:border-primary/40',
                        item.uploading && 'pointer-events-none opacity-50',
                        item.imageUrl && 'border-primary/30',
                      )}>
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">{item.uploading ? 'Enviando...' : 'Upload'}</span>
                        <input
                          type="file"
                          accept="image/*,video/mp4,video/quicktime,video/webm"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && handleContentUpload(item.id, e.target.files[0])}
                        />
                      </label>

                      <div className="flex-1 space-y-1.5">
                        <Input
                          value={item.imageUrl}
                          onChange={(e) => updateContent(item.id, 'imageUrl', e.target.value)}
                          placeholder="URL da midia..."
                          className="h-7 text-[11px]"
                        />
                        <div className="relative">
                          <Textarea
                            value={item.message}
                            onChange={(e) => updateContent(item.id, 'message', e.target.value)}
                            placeholder="Legenda..."
                            className="min-h-[50px] resize-none text-[11px] pr-8"
                          />
                          {aiStatus?.connected && (
                            <button type="button" onClick={() => handleGenerateCaption(item.id)}
                              disabled={generateCaptionMut.isPending}
                              title="Gerar legenda com IA"
                              className="absolute right-1.5 top-1.5 rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-50">
                              {generateCaptionMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>
                      </div>

                      {item.imageUrl && (
                        <div className="flex flex-col items-center gap-1">
                          {item.mediaType === 'video' ? (
                            <video src={item.imageUrl} className="h-16 w-16 rounded border border-border object-cover" muted />
                          ) : (
                            <img src={item.imageUrl} alt="" className="h-16 w-16 rounded border border-border object-cover" />
                          )}
                          <Badge variant="outline" className="text-[10px]">
                            {item.mediaType === 'video' ? 'Video' : 'Imagem'}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ========== STEP 2: Account selection ========== */}
          {step === 2 && (
            <div className="space-y-4 pt-4">
              <div>
                <h3 className="text-sm font-medium">Paginas</h3>
                <p className="text-[11px] text-muted-foreground">Selecione as paginas que receberao a campanha</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar conta..."
                    className="h-8 pl-8 text-xs"
                  />
                </div>
                {allTags.length > 0 && (
                  <Select value={tagFilter} onValueChange={setTagFilter}>
                    <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Tag" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas tags</SelectItem>
                      {allTags.map(t => <SelectItem key={t} value={t}>#{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="outline" size="sm" className="h-8 gap-1 text-[11px]" onClick={selectAllFiltered}>
                  <CheckCheck className="h-3 w-3" /> Selecionar {filteredAccounts.length}
                </Button>
                {selectedCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-8 text-[11px] text-muted-foreground" onClick={clearAll}>
                    Limpar
                  </Button>
                )}
              </div>

              <div className="max-h-[350px] space-y-1.5 overflow-y-auto rounded-lg border border-border p-2">
                {filteredAccounts.map(account => {
                  const sel = selectedAccounts[account.igUserId] || { ig: false, fb: false }
                  const anySelected = sel.ig || sel.fb
                  return (
                    <button
                      type="button"
                      key={account.igUserId}
                      onClick={() => {
                        const current = selectedAccounts[account.igUserId] || { ig: false, fb: false }
                        const anyOn = current.ig || current.fb
                        if (anyOn) {
                          setSelectedAccounts(prev => ({ ...prev, [account.igUserId]: { ig: false, fb: false } }))
                        } else {
                          setSelectedAccounts(prev => ({ ...prev, [account.igUserId]: { ig: !account.fbOnly, fb: true } }))
                        }
                      }}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 transition-colors text-left',
                        anySelected ? 'bg-primary/5 ring-1 ring-primary/20' : 'hover:bg-muted/50',
                      )}
                    >
                      <img
                        src={`/api/v2/page-picture/${account.pageId}`}
                        alt=""
                        className="h-7 w-7 shrink-0 rounded-full bg-muted object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <div className="min-w-0 flex-1">
                        <span className="truncate text-xs font-medium">
                          {account.fbOnly ? account.name : `@${account.username}`}
                        </span>
                        {account.tags?.length ? (
                          <span className="ml-1.5 text-[10px] text-muted-foreground/50">#{account.tags[0]}</span>
                        ) : null}
                      </div>
                      <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {!account.fbOnly && (
                          <span
                            role="button"
                            onClick={() => toggleAccount(account.igUserId, 'ig')}
                            className={cn(
                              'flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                              sel.ig
                                ? 'border-purple-500/40 bg-purple-500/15 text-purple-600 dark:text-purple-400'
                                : 'border-border text-muted-foreground hover:border-purple-500/30',
                            )}
                          >
                            <Instagram className="h-2.5 w-2.5" /> IG
                          </span>
                        )}
                        <span
                          role="button"
                          onClick={() => toggleAccount(account.igUserId, 'fb')}
                          className={cn(
                            'flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                            sel.fb
                              ? 'border-blue-500/40 bg-blue-500/15 text-blue-600 dark:text-blue-400'
                              : 'border-border text-muted-foreground hover:border-blue-500/30',
                          )}
                        >
                          <Facebook className="h-2.5 w-2.5" /> FB
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* YouTube Channels */}
              {ytChannels && ytChannels.length > 0 && (
                <div className="mt-3">
                  <div className="mb-2 flex items-center gap-1.5">
                    <Youtube className="h-3.5 w-3.5 text-red-500" />
                    <Label className="text-xs text-muted-foreground">Canais YouTube</Label>
                  </div>
                  <div className="space-y-1.5 rounded-lg border border-border p-2">
                    {ytChannels.map(ch => {
                      const isSelected = !!selectedYtChannels[ch.id]
                      return (
                        <button
                          key={ch.id}
                          type="button"
                          onClick={() => toggleYtChannel(ch.id)}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
                            isSelected ? 'bg-red-500/5' : 'hover:bg-muted/50',
                          )}
                        >
                          {ch.channelThumbnail ? (
                            <img src={ch.channelThumbnail} alt="" className="h-6 w-6 rounded-full" />
                          ) : (
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500/10">
                              <Youtube className="h-3 w-3 text-red-500" />
                            </div>
                          )}
                          <span className="flex-1 truncate text-xs font-medium">{ch.channelTitle}</span>
                          {isSelected && (
                            <Badge className="shrink-0 bg-red-500/15 text-red-600 text-[10px] border-red-500/30">YT</Badge>
                          )}
                        </button>
                      )
                    })}
                  </div>

                  {/* YouTube fields — tags + visibilidade only (titulo/descricao vem do caption + config) */}
                  {hasYTSelected && (
                    <div className="mt-3 space-y-2.5 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                      <p className="text-[11px] text-muted-foreground">Titulo = legenda do post | Descricao = config do canal</p>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="mb-1 text-xs text-muted-foreground">Tags</Label>
                          <Input
                            value={ytTags}
                            onChange={(e) => setYtTags(e.target.value)}
                            placeholder="tag1, tag2..."
                            className="h-7 text-xs"
                          />
                        </div>
                        <div className="w-28">
                          <Label className="mb-1 text-xs text-muted-foreground">Visibilidade</Label>
                          <Select value={ytPrivacy} onValueChange={(v) => setYtPrivacy(v as 'public' | 'unlisted' | 'private')}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="public">Publico</SelectItem>
                              <SelectItem value="unlisted">Nao listado</SelectItem>
                              <SelectItem value="private">Privado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Profile selector */}
              {availableProfiles.length > 0 && selectedCount > 0 && (
                <div className="rounded-lg border border-border p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">Perfil preferencial:</label>
                    <Select value={preferredProfile || '__auto__'} onValueChange={v => setPreferredProfile(v === '__auto__' ? '' : v)}>
                      <SelectTrigger className="h-8 flex-1 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__auto__">Automatico (primeiro disponivel)</SelectItem>
                        {availableProfiles.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Se o perfil falhar, outro vinculado sera usado automaticamente.</p>
                </div>
              )}

              {selectedCount > 0 && (
                <p className="text-[11px] text-primary">
                  {selectedPageCount} pagina{selectedPageCount !== 1 && 's'}, {selectedCount} destino{selectedCount !== 1 && 's'}
                </p>
              )}
            </div>
          )}

          {/* ========== STEP 3: Schedule ========== */}
          {step === 3 && (
            <div className="space-y-4 pt-4">
              <div>
                <h3 className="text-sm font-medium">Agenda</h3>
                <p className="text-[11px] text-muted-foreground">
                  {clipMode ? 'Configure data e horarios dos cortes' : 'Configure data, horarios e intervalo'}
                </p>
              </div>

              {/* Clip mode schedule */}
              {clipMode && folderClips ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Scissors className="h-4 w-4 text-primary" />
                      <p className="text-xs font-medium text-primary">Campanha de Cortes</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      1 video por dia. Cortes de manha + tarde (max 5 cada). 1 min entre posts. Sem stories.
                      {clipDistribution === 'shuffled' && selectedPageCount > 1 && (
                        <span className="block mt-1 text-violet-400 font-medium">
                          Embaralhado: cada pagina recebe clips diferentes ({Math.ceil(Object.values(folderClips).reduce((sum, c) => sum + c.length, 0) / selectedPageCount)} por pagina)
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <Label className="mb-1.5 text-xs text-muted-foreground">Data inicio</Label>
                      <Input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="mb-1.5 text-xs text-muted-foreground">Horario manha</Label>
                      <Input
                        type="time"
                        value={clipMorningTime}
                        onChange={(e) => setClipMorningTime(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="mb-1.5 text-xs text-muted-foreground">Horario tarde</Label>
                      <Input
                        type="time"
                        value={clipAfternoonTime}
                        onChange={(e) => setClipAfternoonTime(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>

                  {/* Preview schedule per day */}
                  {clipDistribution === 'shuffled' && selectedPageCount > 1 ? (
                    /* Shuffle preview: show matrix distribution summary */
                    (() => {
                      const totalClips = Object.values(folderClips).reduce((sum, c) => sum + c.length, 0)
                      const clipsPerPage = Math.ceil(totalClips / selectedPageCount)
                      const daysPerPage = Math.ceil(clipsPerPage / 10)
                      const selectedAccountNames = Object.entries(selectedAccounts)
                        .filter(([, v]) => v.ig || v.fb)
                        .map(([igUserId]) => {
                          const acct = accounts.find(a => a.igUserId === igUserId)
                          return acct?.fbOnly ? (acct.name || igUserId) : `@${acct?.username || igUserId}`
                        })
                      return (
                        <div className="space-y-2">
                          <p className="text-[11px] font-medium text-violet-400">Distribuicao embaralhada:</p>
                          <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3 space-y-1.5">
                            <div className="flex items-center justify-between text-[11px]">
                              <span className="text-muted-foreground">Total de clips</span>
                              <span className="font-medium">{totalClips}</span>
                            </div>
                            <div className="flex items-center justify-between text-[11px]">
                              <span className="text-muted-foreground">Paginas</span>
                              <span className="font-medium">{selectedPageCount}</span>
                            </div>
                            <div className="flex items-center justify-between text-[11px]">
                              <span className="text-muted-foreground">Clips por pagina</span>
                              <span className="font-medium">~{clipsPerPage}</span>
                            </div>
                            <div className="flex items-center justify-between text-[11px]">
                              <span className="text-muted-foreground">Dias de publicacao</span>
                              <span className="font-medium">{daysPerPage}</span>
                            </div>
                          </div>
                          <div className="space-y-0.5">
                            {selectedAccountNames.slice(0, 5).map((name, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-[10px]">
                                <span className="w-24 truncate font-medium">{name}</span>
                                <span className="text-muted-foreground">~{clipsPerPage} clips aleatorios</span>
                              </div>
                            ))}
                            {selectedAccountNames.length > 5 && (
                              <p className="text-[10px] text-muted-foreground/50">+{selectedAccountNames.length - 5} paginas...</p>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground">Todos os {totalClips} clips serao postados. Nenhum fica de fora.</p>
                        </div>
                      )
                    })()
                  ) : (
                    /* Original preview: same order for all pages */
                    Object.entries(folderClips).map(([source, clips], dayIdx) => {
                      const half = Math.ceil(clips.length / 2)
                      const morning = clips.slice(0, Math.min(half, 5))
                      const afternoon = clips.slice(Math.min(half, 5), Math.min(half, 5) + 5)
                      const startD = scheduleDate ? new Date(scheduleDate + 'T12:00:00') : null
                      let dayLabel = `Dia ${dayIdx + 1}`
                      if (startD) {
                        const d = new Date(startD)
                        d.setDate(d.getDate() + dayIdx)
                        dayLabel = d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })
                      }
                      return (
                        <div key={source} className="rounded-lg border border-border p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{dayLabel}</Badge>
                            <span className="text-[11px] font-medium">{source}</span>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-2">
                            <div>
                              <p className="text-[10px] font-medium text-muted-foreground mb-0.5">Manha ({clipMorningTime})</p>
                              {morning.map((c, i) => (
                                <div key={c.id} className="flex items-center gap-1.5 text-[10px] py-0.5">
                                  <Badge className="bg-primary/10 text-primary border-primary/20 text-[7px] px-1 py-0">
                                    {c.clip!.part}/{c.clip!.total}
                                  </Badge>
                                  <span className="text-muted-foreground">
                                    {(() => {
                                      const [h, m] = clipMorningTime.split(':').map(Number)
                                      const t = h * 60 + m + i
                                      return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
                                    })()}
                                  </span>
                                  <span className="text-muted-foreground/50">{c.clip!.duration}s</span>
                                </div>
                              ))}
                            </div>
                            {afternoon.length > 0 && (
                              <div>
                                <p className="text-[10px] font-medium text-muted-foreground mb-0.5">Tarde ({clipAfternoonTime})</p>
                                {afternoon.map((c, i) => (
                                  <div key={c.id} className="flex items-center gap-1.5 text-[10px] py-0.5">
                                    <Badge className="bg-primary/10 text-primary border-primary/20 text-[7px] px-1 py-0">
                                      {c.clip!.part}/{c.clip!.total}
                                    </Badge>
                                    <span className="text-muted-foreground">
                                      {(() => {
                                        const [h, m] = clipAfternoonTime.split(':').map(Number)
                                        const t = h * 60 + m + i
                                        return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
                                      })()}
                                    </span>
                                    <span className="text-muted-foreground/50">{c.clip!.duration}s</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              ) : (
              <>
              {/* Multi-day toggle */}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div className="flex items-center gap-2">
                  <CalendarRange className="h-4 w-4 text-primary" />
                  <div>
                    <p className="text-xs font-medium">Modo multi-dia</p>
                    <p className="text-[11px] text-muted-foreground">{imagesPerDay} post{imagesPerDay > 1 ? 's' : ''}/dia/conta, imagens embaralhadas por dia</p>
                  </div>
                </div>
                <Switch checked={multiDay} onCheckedChange={setMultiDay} />
              </div>

              {multiDay ? (
                /* Multi-day fields */
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <Label className="mb-1.5 text-xs text-muted-foreground">Data inicio</Label>
                      <Input
                        type="date"
                        value={multiDayStart}
                        onChange={(e) => setMultiDayStart(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="mb-1.5 text-xs text-muted-foreground">Numero de dias</Label>
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={totalDays || ''}
                        onChange={(e) => setTotalDays(Number(e.target.value) || 0)}
                        onBlur={() => setTotalDays(v => Math.max(1, v))}
                        className="h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="mb-1.5 text-xs text-muted-foreground">Horario inicio</Label>
                      <Input
                        type="time"
                        value={postTime}
                        onChange={(e) => setPostTime(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <Label className="mb-1.5 text-xs text-muted-foreground">Imagens por dia</Label>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        value={imagesPerDay || ''}
                        onChange={(e) => setImagesPerDay(Number(e.target.value) || 0)}
                        onBlur={() => setImagesPerDay(v => Math.max(1, v))}
                        className="h-9 text-xs"
                      />
                    </div>
                    {imagesPerDay > 1 && (
                      <>
                        <div>
                          <Label className="mb-1.5 text-xs text-muted-foreground">Intervalo min (min)</Label>
                          <Input
                            type="number"
                            min={1}
                            max={intervalMax}
                            value={intervalMin || ''}
                            onChange={(e) => setIntervalMin(Number(e.target.value) || 0)}
                            onBlur={() => setIntervalMin(v => Math.max(1, v))}
                            className="h-9 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="mb-1.5 text-xs text-muted-foreground">Intervalo max (min)</Label>
                          <Input
                            type="number"
                            min={intervalMin}
                            max={1440}
                            value={intervalMax || ''}
                            onChange={(e) => setIntervalMax(Number(e.target.value) || 0)}
                            onBlur={() => setIntervalMax(v => Math.max(intervalMin || 1, v))}
                            className="h-9 text-xs"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {contentSource === 'library' && selectedFolderData && imagesPerDay > 1 && (
                    <p className="text-[11px] text-muted-foreground">
                      Total: {imagesPerDay * totalDays} imagens necessarias ({imagesPerDay}/dia x {totalDays} dias)
                      {imagesPerDay * totalDays > selectedImageIds.size && (
                        <span className="text-amber-500"> — {selectedImageIds.size} imagens selecionadas (sera perguntado se reciclar)</span>
                      )}
                    </p>
                  )}
                </div>
              ) : (
                /* Original single-day fields */
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1.5 text-xs text-muted-foreground">Data</Label>
                    <Input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="h-9 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 text-xs text-muted-foreground">Intervalo (minutos)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={120}
                      value={intervalMinutes || ''}
                      onChange={(e) => setIntervalMinutes(Number(e.target.value) || 0)}
                      onBlur={() => setIntervalMinutes(v => Math.max(1, v))}
                      className="h-9 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 text-xs text-muted-foreground">Horario inicio</Label>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="h-9 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 text-xs text-muted-foreground">Horario fim</Label>
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="h-9 text-xs"
                    />
                  </div>
                </div>
              )}
              </>
              )}

              {!clipMode && (
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-xs font-medium">Incluir Stories</p>
                    <p className="text-[11px] text-muted-foreground">
                      {multiDay ? 'Stories diarios pra todas paginas' : 'Stories vao no horario inicio pra todas paginas'}
                    </p>
                  </div>
                  <Switch checked={includeStories} onCheckedChange={setIncludeStories} />
                </div>
              )}

              {!clipMode && !multiDay && includeStories && contentItems.length > 1 && (
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">Conteudo do Story</Label>
                  <Select value={String(storyContentIndex)} onValueChange={(v) => setStoryContentIndex(Number(v))}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {contentItems.filter(c => c.imageUrl).map((c, i) => (
                        <SelectItem key={c.id} value={String(i)}>
                          Conteudo {i + 1} {c.message ? `- ${c.message.slice(0, 30)}...` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="mb-1.5 text-xs text-muted-foreground">Categoria</Label>
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Ex: Campanha"
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}

          {/* ========== STEP 4: Preview ========== */}
          {step === 4 && (
            <div className="space-y-4 pt-4">
              <div>
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" /> Preview da Campanha
                </h3>
                <p className="text-[11px] text-muted-foreground">Revise e ajuste cada conteudo antes de enviar</p>
              </div>

              {/* Stats summary */}
              <div className="flex gap-2">
                <div className="flex-1 rounded-lg border border-border p-2 text-center">
                  <p className="text-base font-bold text-primary">{selectedPageCount}</p>
                  <p className="text-[10px] text-muted-foreground">Paginas</p>
                </div>
                <div className="flex-1 rounded-lg border border-border p-2 text-center">
                  <p className="text-base font-bold text-primary">{preview.totalPosts}</p>
                  <p className="text-[10px] text-muted-foreground">Posts</p>
                </div>
                {multiDay && (
                  <div className="flex-1 rounded-lg border border-border p-2 text-center">
                    <p className="text-base font-bold text-primary">{totalDays}</p>
                    <p className="text-[10px] text-muted-foreground">Dias</p>
                  </div>
                )}
                {hasYTSelected && (
                  <div className="flex-1 rounded-lg border border-border p-2 text-center">
                    <p className="text-base font-bold text-red-500"><Youtube className="h-4 w-4 mx-auto" /></p>
                    <p className="text-[10px] text-muted-foreground">YouTube</p>
                  </div>
                )}
              </div>

              {/* Personalizar Titulos button */}
              {contentSource !== 'library' && contentItems.filter(c => c.imageUrl).length > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => setTitleReviewOpen(true)}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Personalizar Titulos
                  </Button>
                  {contentItems.some(c => c.imageUrl && c.title) && (
                    <span className="text-[11px] text-muted-foreground">
                      {contentItems.filter(c => c.imageUrl && c.title).length}/{contentItems.filter(c => c.imageUrl).length} com titulo
                    </span>
                  )}
                </div>
              )}

              {/* Content items preview - editable */}
              {contentSource !== 'library' && contentItems.filter(c => c.imageUrl).length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-medium">Conteudos ({contentItems.filter(c => c.imageUrl).length})</p>
                  {contentItems.filter(c => c.imageUrl).map((c, i) => (
                    <div key={c.id} className="overflow-hidden rounded-lg border border-border">
                      <div className="flex gap-3 p-3">
                        {/* Thumbnail */}
                        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md bg-muted">
                          {c.mediaType === 'video' ? (
                            <video
                              src={c.imageUrl}
                              className="h-full w-full object-cover"
                              muted
                              preload="metadata"
                            />
                          ) : (
                            <img src={c.imageUrl} alt="" className="h-full w-full object-cover" />
                          )}
                          <div className="absolute bottom-0.5 left-0.5">
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 gap-0.5">
                              {c.mediaType === 'video' ? <Video className="h-2 w-2" /> : <ImageIcon className="h-2 w-2" />}
                              {i + 1}
                            </Badge>
                          </div>
                        </div>

                        {/* Editable fields */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          {/* Platform badges + title indicator */}
                          <div className="flex items-center gap-1">
                            {Object.values(selectedAccounts).some(v => v.ig) && (
                              <Badge variant="outline" className="h-4 text-[10px] gap-0.5 px-1">
                                <Instagram className="h-2 w-2" /> IG
                              </Badge>
                            )}
                            {Object.values(selectedAccounts).some(v => v.fb) && (
                              <Badge variant="outline" className="h-4 text-[10px] gap-0.5 px-1 border-blue-500/30 text-blue-600">
                                <Facebook className="h-2 w-2" /> FB
                              </Badge>
                            )}
                            {hasYTSelected && (
                              <Badge variant="outline" className="h-4 text-[10px] gap-0.5 px-1 border-red-500/30 text-red-600">
                                <Youtube className="h-2 w-2" /> YT
                              </Badge>
                            )}
                            {c.title && (
                              <Badge variant="outline" className="h-4 text-[10px] gap-0.5 px-1 border-green-500/30 text-green-600">
                                <Pencil className="h-2 w-2" /> Titulo
                              </Badge>
                            )}
                          </div>

                          {/* Caption */}
                          <div className="relative">
                            <Textarea
                              value={c.message}
                              onChange={(e) => updateContent(c.id, 'message', e.target.value)}
                              placeholder="Legenda / descricao..."
                              className="min-h-[48px] resize-none text-[11px] leading-relaxed pr-8"
                            />
                            {aiStatus?.connected && (
                              <button type="button" onClick={() => handleGenerateCaption(c.id)}
                                disabled={generateCaptionMut.isPending}
                                title="Gerar legenda com IA"
                                className="absolute right-1.5 top-1.5 rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-50">
                                {generateCaptionMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Library mode info */}
              {contentSource === 'library' && selectedFolderData && (
                <div className="rounded-lg border border-border p-3 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium">{selectedFolderData.name}</span>
                    <span className="text-[11px] text-muted-foreground">({selectedImageIds.size} de {selectedFolderData.images.length} selecionadas)</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {clipMode
                      ? `Modo cortes: ${Object.keys(folderClips || {}).length} dias de publicacao`
                      : `${selectedImageIds.size} imagens selecionadas`
                    }
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Teor: {dynamicTones.find(t => t.value === selectedTone)?.label || '-'}
                  </p>
                </div>
              )}

              {/* YouTube fields (if YT channels selected) */}
              {hasYTSelected && (
                <div className="space-y-2 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                  <div className="flex items-center gap-1.5">
                    <Youtube className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-xs font-medium">YouTube</span>
                  </div>
                  <div className="space-y-1.5">
                    <Input
                      value={ytTitle}
                      onChange={(e) => setYtTitle(e.target.value)}
                      placeholder="Titulo do video (YouTube)..."
                      className="h-8 text-xs"
                    />
                    <Textarea
                      value={ytDescription}
                      onChange={(e) => setYtDescription(e.target.value)}
                      placeholder="Descricao do video (YouTube)..."
                      className="min-h-[48px] resize-none text-xs"
                    />
                    <Input
                      value={ytTags}
                      onChange={(e) => setYtTags(e.target.value)}
                      placeholder="Tags separadas por virgula..."
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              )}

              {/* Schedule info */}
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1 text-[11px] text-muted-foreground">
                {multiDay ? (
                  <>
                    <p>Multi-dia: {multiDayStart ? new Date(multiDayStart + 'T12:00:00').toLocaleDateString('pt-BR') : '-'} a {preview.endDate ? new Date(preview.endDate + 'T12:00:00').toLocaleDateString('pt-BR') : '-'} | {totalDays} dias | Inicio as {postTime}</p>
                    {imagesPerDay > 1 && <p>Intervalo: {intervalMin}-{intervalMax} min entre posts</p>}
                  </>
                ) : clipMode ? (
                  <p>Cortes: {scheduleDate} | Manha: {clipMorningTime} | Tarde: {clipAfternoonTime}</p>
                ) : (
                  <p>Data: {scheduleDate} | {startTime} - {endTime} | Intervalo: {intervalMinutes}min</p>
                )}
                <p>Categoria: {category || '-'} | Teor: {dynamicTones.find(t => t.value === selectedTone)?.label || '-'}</p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-5">
            {step > 1 && (
              <Button variant="outline" className="gap-1" onClick={() => setStep((step - 1) as 1 | 2 | 3 | 4)}>
                <ChevronLeft className="h-4 w-4" /> Voltar
              </Button>
            )}
            <div className="flex-1" />
            {step < 4 ? (
              <Button
                className="gap-1"
                disabled={!canNext()}
                onClick={() => setStep((step + 1) as 1 | 2 | 3 | 4)}
              >
                Proximo <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (<>
              {Object.values(selectedAccounts).some(v => v.fb) && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={ctaSendMessage} onChange={e => setCtaSendMessage(e.target.checked)}
                    className="rounded border-border" />
                  <span className={`text-xs whitespace-nowrap ${ctaSendMessage ? 'text-blue-400 font-semibold' : 'text-muted-foreground'}`}>
                    Botao "Enviar Mensagem" (FB)
                  </span>
                </label>
              )}
              <Button
                className="gap-2"
                disabled={createCampaign.isPending}
                onClick={handleSubmit}
              >
                <Rocket className="h-4 w-4" />
                {createCampaign.isPending ? 'Criando...' : 'Criar Campanha'}
              </Button>
            </>)}
          </div>
        </div>
      </DialogContent>
      <CaptionPresetEditor open={presetEditorOpen} onOpenChange={setPresetEditorOpen} />

      {/* Title Review Modal */}
      <Dialog open={titleReviewOpen} onOpenChange={setTitleReviewOpen}>
        <DialogContent
          className="max-h-[90vh] max-w-4xl overflow-y-auto p-0"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <VisuallyHidden><DialogTitle>Revisar Titulos</DialogTitle></VisuallyHidden>

          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">Revisar Titulos</h2>
              <Badge variant="secondary" className="text-xs">{contentItems.filter(c => c.imageUrl).length} videos</Badge>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"><XIcon className="h-4 w-4" /></Button>
            </DialogClose>
          </div>

          <div className="space-y-4 px-5 py-4">
            {/* Apply to all section */}
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
              <p className="text-xs font-medium flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-primary" /> Aplicar para todos
              </p>
              <div className="flex gap-2">
                <Textarea
                  value={bulkDescription}
                  onChange={(e) => setBulkDescription(e.target.value)}
                  placeholder="Descricao template para aplicar a todos..."
                  className="flex-1 min-h-[48px] resize-none text-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 self-end"
                  onClick={() => {
                    setContentItems(prev => prev.map(c => c.imageUrl ? { ...c, description: bulkDescription } : c))
                    toast.success('Descricao aplicada a todos!')
                  }}
                >
                  Aplicar
                </Button>
              </div>
              {hasYTSelected && ytDefaultDesc?.defaultDescription && (
                <div className="flex items-start gap-2 rounded-md bg-blue-500/10 border border-blue-500/20 p-2.5">
                  <Youtube className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-blue-700 dark:text-blue-400">
                      Voce ja tem uma descricao padrao configurada para <span className="font-medium">{firstSelectedYtChannel?.channelTitle || 'canal'}</span>
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 mt-1 text-[11px] text-blue-600 hover:text-blue-700 px-2"
                      onClick={() => {
                        setContentItems(prev => prev.map(c => c.imageUrl ? { ...c, description: ytDefaultDesc.defaultDescription } : c))
                        setBulkDescription(ytDefaultDesc.defaultDescription)
                        toast.success('Descricao padrao do YouTube aplicada!')
                      }}
                    >
                      Usar descricao padrao
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Individual items */}
            <div className="space-y-2">
              {contentItems.filter(c => c.imageUrl).map((c, i) => (
                <div key={c.id} className="flex items-start gap-3 rounded-lg border border-border p-3">
                  {/* Thumbnail */}
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
                    {c.mediaType === 'video' ? (
                      <video src={c.imageUrl} className="h-full w-full object-cover" muted preload="metadata" />
                    ) : (
                      <img src={c.imageUrl} alt="" className="h-full w-full object-cover" />
                    )}
                    <div className="absolute bottom-0.5 left-0.5">
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 gap-0.5">
                        {c.mediaType === 'video' ? <Video className="h-2 w-2" /> : <ImageIcon className="h-2 w-2" />}
                        {i + 1}
                      </Badge>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <Label className="text-[11px] text-muted-foreground mb-1">Titulo</Label>
                    <Input
                      value={c.title}
                      onChange={(e) => updateContent(c.id, 'title', e.target.value)}
                      placeholder="Titulo do video..."
                      className="h-8 text-xs"
                    />
                  </div>

                  {/* Description */}
                  <div className="flex-1 min-w-0">
                    <Label className="text-[11px] text-muted-foreground mb-1">Descricao</Label>
                    <Textarea
                      value={c.description}
                      onChange={(e) => updateContent(c.id, 'description', e.target.value)}
                      placeholder="Descricao (opcional)..."
                      className="min-h-[32px] h-8 resize-none text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end border-t border-border px-5 py-3">
            <Button size="sm" onClick={() => setTitleReviewOpen(false)}>
              <CheckCheck className="h-3.5 w-3.5 mr-1.5" /> Concluido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
